/**
 * Add Baby Modal Component
 * Bottom sheet modal for first-time baby registration
 */

import { useState, useCallback } from 'react';
import { useBaby } from '@/contexts/BabyContext';
import { Camera, Check, Calendar } from 'lucide-react';
import Cropper from 'react-easy-crop';
import { getCroppedImg, compressImage } from '@/services/storage.service';
import { toast } from 'sonner';
import { uploadBabyPhoto } from '@/services/storage.service';
import type { BabyInput } from '@/types/baby';
import { Analytics } from '@/lib/analytics';
import babyBoyAvatar from '@/assets/baby_boy.webp';
import babyGirlAvatar from '@/assets/baby-girl-avatar.webp';

interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface AddBabyModalProps {
  isOpen: boolean;
  onClose?: () => void;
}

const AddBabyModal = ({ isOpen, onClose }: AddBabyModalProps) => {
  const { addBaby } = useBaby();

  // Form state
  const [name, setName] = useState('');
  const [dob, setDob] = useState('');
  const [gender, setGender] = useState<'boy' | 'girl' | null>(null);

  // Image upload state
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<CropArea | null>(
    null
  );
  const [showCropper, setShowCropper] = useState(false);

  // Loading state
  const [isLoading, setIsLoading] = useState(false);

  // Handle image selection
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result as string);
      setShowCropper(true);
    };
    reader.readAsDataURL(file);
  };

  // Handle crop complete
  const onCropComplete = useCallback(
    (_croppedArea: any, croppedAreaPixels: CropArea) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  // Save cropped image
  const handleSaveCrop = () => {
    setShowCropper(false);
  };

  // Cancel crop
  const handleCancelCrop = () => {
    setShowCropper(false);
    setImageSrc(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!name.trim()) {
      toast.error('Please enter a name for your baby');
      return;
    }

    if (!dob) {
      toast.error('Please select a date of birth');
      return;
    }

    if (!gender) {
      toast.error('Please select a gender');
      return;
    }

    try {
      setIsLoading(true);

      let photoURL: string | null = null;

      // Upload photo if selected
      if (imageSrc && croppedAreaPixels) {
        try {
          const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
          const compressedBlob = await compressImage(
            new File([croppedBlob], 'photo.jpg'),
            800,
            800,
            0.8
          );
          const tempId = `temp_${Date.now()}`;
          photoURL = await uploadBabyPhoto(tempId, compressedBlob);
        } catch (err) {
          console.error('Error processing image:', err);
        }
      }

      // Create baby data
      const babyData: BabyInput = {
        name: name.trim(),
        dob: new Date(dob),
        gender,
        photoURL,
      };

      // Add baby
      const newBaby = await addBaby(babyData);
      Analytics.addBaby();

      // If photo was uploaded with temp ID, re-upload with actual baby ID
      if (photoURL && imageSrc && croppedAreaPixels) {
        try {
          const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
          const compressedBlob = await compressImage(
            new File([croppedBlob], 'photo.jpg'),
            800,
            800,
            0.8
          );
          const finalPhotoURL = await uploadBabyPhoto(newBaby.id, compressedBlob);

          if (finalPhotoURL !== photoURL) {
            await import('@/services/baby.service').then(({ updateBaby }) =>
              updateBaby(newBaby.id, { photoURL: finalPhotoURL })
            );
          }
        } catch (err) {
          console.error('Error uploading final photo:', err);
        }
      }

      // Close modal or call onClose
      if (onClose) {
        onClose();
      }
    } catch (err: any) {
      console.error('Error adding baby:', err);
      toast.error(err.message || 'Failed to add baby. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <>
      {/* Image Cropper Modal */}
      {showCropper && imageSrc && (
        <div className="fixed inset-0 bg-black z-[60] flex flex-col">
          <div className="bg-black/50 p-4 flex items-center justify-between">
            <button
              onClick={handleCancelCrop}
              className="text-white px-4 py-2 rounded-lg hover:bg-white/10"
            >
              Cancel
            </button>
            <h2 className="text-white font-semibold">Crop Photo</h2>
            <button
              onClick={handleSaveCrop}
              className="text-white px-4 py-2 rounded-lg hover:bg-white/10 flex items-center gap-2"
            >
              <Check className="w-5 h-5" />
              Done
            </button>
          </div>
          <div className="flex-1 relative">
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
          <div className="bg-black/50 p-6">
            <div className="max-w-md mx-auto">
              <label className="text-white text-sm block mb-2">Zoom</label>
              <input
                type="range"
                min={1}
                max={3}
                step={0.1}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="w-full"
              />
            </div>
          </div>
        </div>
      )}

      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 z-[55]" />

      {/* Bottom Sheet Modal */}
      <div className="fixed inset-x-0 bottom-0 z-[60] bg-white rounded-t-3xl max-h-[90vh] flex flex-col animate-slide-up" style={{ boxShadow: 'var(--shadow-modal)' }}>
        {/* Handle Bar */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1 bg-muted rounded-full" />
        </div>

        {/* Header */}
        <div className="px-5 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Add Baby Profile</h2>
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Photo Upload */}
            <div className="flex flex-col items-center">
              <div className="relative">
                {imageSrc && !showCropper ? (
                  <div className="w-20 h-20 rounded-full overflow-hidden bg-muted">
                    <img
                      src={imageSrc}
                      alt="Baby"
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center">
                    <span className="text-4xl">👶</span>
                  </div>
                )}
                <label className="absolute bottom-0 right-0 w-7 h-7 rounded-full flex items-center justify-center cursor-pointer transition-colors" style={{ background: 'hsl(var(--primary))' }}>
                  <Camera className="w-3.5 h-3.5 text-white" />
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                </label>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Tap to add photo (optional)
              </p>
            </div>

            {/* Name Input */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Baby's Name <span style={{ color: 'hsl(var(--destructive))' }}>*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter baby's name"
                className="w-full px-4 py-2.5 bg-white border border-border rounded-xl focus:border-primary focus:outline-none transition-colors text-foreground"
                disabled={isLoading}
              />
            </div>

            {/* Date of Birth */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Date of Birth <span style={{ color: 'hsl(var(--destructive))' }}>*</span>
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={dob}
                  onChange={(e) => setDob(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-2.5 pl-10 bg-white border border-border rounded-xl focus:border-primary focus:outline-none transition-colors text-foreground"
                  disabled={isLoading}
                />
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              </div>
            </div>

            {/* Gender Selection */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Gender <span style={{ color: 'hsl(var(--destructive))' }}>*</span>
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setGender('boy')}
                  disabled={isLoading}
                  className="p-4 rounded-xl border-2 transition-all"
                  style={gender === 'boy'
                    ? { borderColor: 'hsl(var(--primary))', background: 'hsl(var(--primary) / 0.08)' }
                    : { borderColor: 'hsl(var(--border))', background: 'white' }}
                >
                  <div className="flex flex-col items-center gap-1">
                    <img
                      src={babyBoyAvatar}
                      alt="Boy"
                      className="w-20 h-20 rounded-full object-cover"
                    />
                    <span
                      className="text-sm font-medium"
                      style={{ color: gender === 'boy' ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))' }}
                    >
                      😊 Boy
                    </span>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setGender('girl')}
                  disabled={isLoading}
                  className="p-4 rounded-xl border-2 transition-all"
                  style={gender === 'girl'
                    ? { borderColor: 'hsl(var(--primary))', background: 'hsl(var(--primary) / 0.08)' }
                    : { borderColor: 'hsl(var(--border))', background: 'white' }}
                >
                  <div className="flex flex-col items-center gap-1">
                    <img
                      src={babyGirlAvatar}
                      alt="Girl"
                      className="w-20 h-20 rounded-full object-cover"
                    />
                    <span
                      className="text-sm font-medium"
                      style={{ color: gender === 'girl' ? 'hsl(var(--primary))' : 'hsl(var(--muted-foreground))' }}
                    >
                      👑 Girl
                    </span>
                  </div>
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Bottom Button */}
        <div className="px-5 py-4 border-t border-border bg-white">
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="w-full py-3.5 text-white font-medium rounded-xl active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'hsl(var(--primary))' }}
          >
            {isLoading ? 'Saving...' : 'Save Baby Profile'}
          </button>
        </div>
      </div>
    </>
  );
};

export default AddBabyModal;
